import React, { useState } from 'react';
import { Eye, EyeOff, Lock, Mail, KeyRound, ShieldCheck, ArrowLeft, Loader2 } from 'lucide-react';
import { loginAdmin, forgotPassword, resetPassword } from '../api/authApi';

// ─── View states ──────────────────────────────────────────────────────────────
const VIEW = {
  LOGIN: 'login',
  FORGOT: 'forgot',
  RESET: 'reset',
};

const Login = ({ onLogin }) => {
  // ── Shared ──────────────────────────────────────────────────────────────────
  const [view, setView] = useState(VIEW.LOGIN);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // ── Login form ──────────────────────────────────────────────────────────────
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // ── Forgot password form ────────────────────────────────────────────────────
  const [forgotEmail, setForgotEmail] = useState('');

  // ── Reset password form ─────────────────────────────────────────────────────
  const [resetEmail, setResetEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);

  // ── Helpers ─────────────────────────────────────────────────────────────────
  const clearMessages = () => { setError(''); setSuccess(''); };

  const goTo = (v) => {
    clearMessages();
    setView(v);
  };

  // ── Handlers ────────────────────────────────────────────────────────────────

  /** LOGIN */
  const handleLogin = async (e) => {
    e.preventDefault();
    clearMessages();
    setLoading(true);
    try {
      const data = await loginAdmin(email, password);

      // ── Debug: log full backend response ──────────────────────────────────
      console.log('✅ Login API response:', data);

      // ── Extract token — support multiple common backend shapes ────────────
      // Shape 1: { token, user }           (most common)
      // Shape 2: { accessToken, user }
      // Shape 3: { data: { token, user } } (nested)
      // Shape 4: { token, admin }
      const token =
        data.token ||
        data.accessToken ||
        data.data?.token ||
        data.data?.accessToken ||
        null;

      const userObj =
        data.user ||
        data.admin ||
        data.data?.user ||
        data.data?.admin ||
        {};

      if (token) {
        localStorage.setItem('adminToken', token);
        localStorage.setItem('adminUser', JSON.stringify(userObj));
        console.log('🔑 Token saved. User:', userObj);
      } else {
        // Login succeeded but no token found — log the shape for diagnosis
        console.warn('⚠️ Login success but no token found in response. Full data:', data);
      }

      // ── Redirect to dashboard ─────────────────────────────────────────────
      const role = userObj?.role || 'admin';
      onLogin(role);

    } catch (err) {
      console.error('❌ Login error:', err.response || err);
      setError(
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.response?.data?.errors?.[0]?.msg ||
        (err.message === 'Network Error'
          ? 'Cannot reach the server. Is the backend running on port 5000?'
          : 'Invalid email or password. Please try again.')
      );
    } finally {
      setLoading(false);
    }
  };

  /** FORGOT PASSWORD — sends OTP */
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    clearMessages();
    setLoading(true);
    try {
      await forgotPassword(forgotEmail);
      setSuccess('OTP sent to your email. Please check your inbox.');
      // Pre-fill the reset email and move to reset view
      setResetEmail(forgotEmail);
      setTimeout(() => goTo(VIEW.RESET), 1500);
    } catch (err) {
      setError(
        err.response?.data?.message ||
        err.response?.data?.error ||
        'Could not send OTP. Please check the email and try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  /** RESET PASSWORD */
  const handleResetPassword = async (e) => {
    e.preventDefault();
    clearMessages();
    if (otp.length !== 6) {
      setError('OTP must be exactly 6 digits.');
      return;
    }
    if (newPassword.length < 6) {
      setError('New password must be at least 6 characters.');
      return;
    }
    setLoading(true);
    try {
      await resetPassword(resetEmail, otp, newPassword);
      setSuccess('Password reset successful! Redirecting to login...');
      setTimeout(() => goTo(VIEW.LOGIN), 2000);
    } catch (err) {
      setError(
        err.response?.data?.message ||
        err.response?.data?.error ||
        'Failed to reset password. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gradient-to-br from-pakistan-dark via-pakistan to-pakistan-light flex items-center justify-center p-4 relative overflow-hidden font-sans">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-[500px] h-[500px] bg-earth opacity-[0.12] rounded-full blur-[120px]" />
        <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] bg-moss opacity-[0.15] rounded-full blur-[120px]" />
      </div>

      <div className="relative bg-cornsilk/95 backdrop-blur-2xl border border-moss/20 rounded-[32px] shadow-[0_32px_80px_rgba(40,54,24,0.35)] p-10 w-full max-w-md animate-fade-in">

        {/* ── Logo ─────────────────────────────────────────────────────────── */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-glow" style={{ background: 'linear-gradient(135deg, #BC6C25, #606C38)' }}>
            <span className="text-cornsilk text-3xl font-extrabold tracking-tighter">M</span>
          </div>
          <h1 className="text-2xl font-extrabold text-gradient tracking-tight">
            Manthuli Admin
          </h1>
          <p className="text-pakistan/55 mt-1.5 text-xs font-semibold uppercase tracking-wider">
            {view === VIEW.LOGIN && 'Store Administration Panel'}
            {view === VIEW.FORGOT && 'Recover Password'}
            {view === VIEW.RESET && 'Set New Password'}
          </p>
        </div>

        {/* ── Alert Messages ───────────────────────────────────────────────── */}
        {error && (
          <div className="mb-5 bg-tiger/10 border border-tiger/25 text-tiger px-4 py-3.5 rounded-2xl text-xs font-semibold leading-relaxed">
            {error}
          </div>
        )}
        {success && (
          <div className="mb-5 bg-moss/10 border border-moss/25 text-moss px-4 py-3.5 rounded-2xl text-xs font-semibold leading-relaxed">
            {success}
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════════ */}
        {/* VIEW: LOGIN                                                        */}
        {/* ══════════════════════════════════════════════════════════════════ */}
        {view === VIEW.LOGIN && (
          <form onSubmit={handleLogin} className="space-y-5">
            {/* Email */}
            <div>
              <label className="ui-label">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-moss/50" size={16} />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); clearMessages(); }}
                  className="ui-input pl-11 pr-4 py-3.5"
                  placeholder="admin@example.com"
                  required
                  autoComplete="email"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="ui-label">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-moss/50" size={16} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); clearMessages(); }}
                  className="ui-input pl-11 pr-12 py-3.5"
                  placeholder="••••••••••••"
                  required
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-moss/50 hover:text-pakistan transition-colors cursor-pointer"
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {/* Forgot link */}
            <div className="text-right">
              <button
                type="button"
                onClick={() => goTo(VIEW.FORGOT)}
                className="text-xs font-bold text-tiger hover:text-tiger-light hover:underline transition-colors cursor-pointer"
              >
                Forgot password?
              </button>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="ui-btn-primary w-full py-3.5 px-4 rounded-2xl font-bold uppercase tracking-wider mt-2 text-xs"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : null}
              {loading ? 'Authenticating...' : 'Sign In'}
            </button>
          </form>
        )}

        {/* ══════════════════════════════════════════════════════════════════ */}
        {/* VIEW: FORGOT PASSWORD                                              */}
        {/* ══════════════════════════════════════════════════════════════════ */}
        {view === VIEW.FORGOT && (
          <form onSubmit={handleForgotPassword} className="space-y-5">
            <div>
              <label className="ui-label">
                Registered Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-moss/50" size={16} />
                <input
                  type="email"
                  value={forgotEmail}
                  onChange={(e) => { setForgotEmail(e.target.value); clearMessages(); }}
                  className="ui-input pl-11 pr-4 py-3.5"
                  placeholder="admin@example.com"
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="ui-btn-primary w-full py-3.5 px-4 rounded-2xl font-bold uppercase tracking-wider text-xs"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : <KeyRound size={16} />}
              {loading ? 'Sending Verification...' : 'Request OTP'}
            </button>

            {/* Back to login */}
            <button
              type="button"
              onClick={() => goTo(VIEW.LOGIN)}
              className="w-full flex items-center justify-center gap-1 text-xs font-bold text-pakistan/50 hover:text-pakistan transition-colors cursor-pointer mt-4"
            >
              <ArrowLeft size={13} /> Back to Sign In
            </button>
          </form>
        )}

        {/* ══════════════════════════════════════════════════════════════════ */}
        {/* VIEW: RESET PASSWORD                                               */}
        {/* ══════════════════════════════════════════════════════════════════ */}
        {view === VIEW.RESET && (
          <form onSubmit={handleResetPassword} className="space-y-5">
            {/* Email (pre-filled) */}
            <div>
              <label className="ui-label">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-moss/50" size={16} />
                <input
                  type="email"
                  value={resetEmail}
                  onChange={(e) => { setResetEmail(e.target.value); clearMessages(); }}
                  className="ui-input pl-11 pr-4 py-3.5"
                  placeholder="admin@example.com"
                  required
                />
              </div>
            </div>

            {/* OTP */}
            <div>
              <label className="ui-label">
                6-digit OTP
              </label>
              <div className="relative">
                <ShieldCheck className="absolute left-4 top-1/2 -translate-y-1/2 text-moss/50" size={16} />
                <input
                  type="text"
                  value={otp}
                  onChange={(e) => { setOtp(e.target.value.replace(/\D/g, '').slice(0, 6)); clearMessages(); }}
                  className="ui-input pl-11 pr-4 py-3 tracking-[0.4em] text-center text-lg font-bold"
                  placeholder="••••••"
                  maxLength={6}
                  required
                />
              </div>
            </div>

            {/* New Password */}
            <div>
              <label className="ui-label">
                New Password
              </label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-moss/50" size={16} />
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => { setNewPassword(e.target.value); clearMessages(); }}
                  className="ui-input pl-11 pr-12 py-3.5"
                  placeholder="Min 6 characters"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-moss/50 hover:text-pakistan transition-colors cursor-pointer"
                >
                  {showNewPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="ui-btn-primary w-full py-3.5 px-4 rounded-2xl font-bold uppercase tracking-wider text-xs"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : null}
              {loading ? 'Saving Password...' : 'Reset Password'}
            </button>

            {/* Resend OTP */}
            <button
              type="button"
              onClick={() => goTo(VIEW.FORGOT)}
              className="w-full flex items-center justify-center gap-1 text-xs font-bold text-pakistan/50 hover:text-pakistan transition-colors cursor-pointer mt-4"
            >
              <ArrowLeft size={13} /> Resend OTP Code
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default Login;